import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import {
  ModalController,
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { DeviceDetectorService } from 'ngx-device-detector';

import { Observable, firstValueFrom } from 'rxjs';

import { PuzzleFrameworkService } from 'projects/tools/src/lib//puzzle-framework-module/puzzle-framework.service';

import { FeedbackEntryModel } from 'projects/tools/src/lib/shared-module/feedback/store/feedback-entry.model';
import { FeedbackState } from 'projects/tools/src/lib/shared-module/feedback/store/feedback.state';

import * as FeedbackActions from 'projects/tools/src/lib/shared-module/feedback/store/feedback.actions';

import { buildEnv } from 'build-environment';
import { addIcons } from 'ionicons';
import { thumbsUpSharp, thumbsDownSharp, sendSharp, closeSharp } from 'ionicons/icons';

const __PROJECT_NAME__: string = buildEnv.projectName;

@Component({
  selector: 'lib-app-feedback-modal',
  templateUrl: './feedback-modal.component.html',
  styleUrls: ['./feedback-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonButton,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonRow,
    IonTextarea,
    IonTitle,
    IonToolbar,
    TranslateModule,
    FormsModule,
  ],
})
export class FeedbackModalComponent {
  @Input() featureId: string;

  private functions: Functions = inject(Functions);

  // Which page are we on? The thumbs up/down page or the text input page?
  public textInputMode = false;
  public text = '';
  public email = '';

  private newFeedbackEntryFunction: (data: any) => Observable<any>;

  constructor(
    private store: Store,
    private framework: PuzzleFrameworkService,
    private ionicModal: ModalController,
    public device: DeviceDetectorService
  ) {
    addIcons({
      thumbsUpSharp,
      thumbsDownSharp,
      sendSharp,
      closeSharp,
    });
    this.newFeedbackEntryFunction = httpsCallableData(this.functions, 'newFeedbackEntry', { timeout: 40_000 });
  }

  public async setScore(score: string) {
    this.store.dispatch(
      new FeedbackActions.SetFeedbackScore({
        projectId: __PROJECT_NAME__,
        puzzleName: this.framework.configPuzzleName,
        featureId: this.featureId,
        score,
      })
    );
    await this.updateFeedbackEntry(this.featureId);
    this.textInputMode = true;
  }

  public async setText() {
    this.store.dispatch(
      new FeedbackActions.SetFeedbackText({
        projectId: __PROJECT_NAME__,
        puzzleName: this.framework.configPuzzleName,
        featureId: this.featureId,
        text: this.text,
        email: this.email,
      })
    );
    await this.updateFeedbackEntry(this.featureId);
    await this.ionicModal.dismiss();
    this.textInputMode = false;
  }

  public async closeModal() {
    await this.ionicModal.dismiss();
  }

  public async updateFeedbackEntry(featureId: string) {
    await firstValueFrom(
      this.store.selectOnce(
        FeedbackState.getByID({
          projectId: __PROJECT_NAME__,
          puzzleName: this.framework.configPuzzleName,
          featureId,
        })
      )
    ).then(async (feedbackEntry: FeedbackEntryModel) => {
      await firstValueFrom(
        this.newFeedbackEntryFunction({
          entryId: feedbackEntry.UUID,
          entry: feedbackEntry,
          projectId: __PROJECT_NAME__,
          puzzleName: this.framework.configPuzzleName,
          featureId,
        })
      );
    });
  }
}
